import { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  handleReload = () => {
    window.location.reload()
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-base-200 p-4">
          <div className="card bg-base-100 shadow-xl max-w-md w-full">
            <div className="card-body text-center">
              <div className="text-6xl mb-4">😥</div>
              <h2 className="card-title justify-center text-xl mb-2">
                문제가 발생했습니다
              </h2>
              <p className="text-base-content/70 mb-4">
                페이지를 표시하는 중 오류가 발생했습니다.
                <br />
                잠시 후 다시 시도해주세요.
              </p>

              {import.meta.env.DEV && this.state.error && (
                <div className="bg-error/10 p-3 rounded-lg mb-4 text-left">
                  <p className="text-xs font-mono text-error break-all">
                    {this.state.error.message}
                  </p>
                </div>
              )}

              <div className="card-actions justify-center gap-2">
                <button
                  onClick={this.handleRetry}
                  className="btn btn-primary btn-sm"
                >
                  다시 시도
                </button>
                <button
                  onClick={this.handleReload}
                  className="btn btn-outline btn-sm"
                >
                  새로고침
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="btn btn-ghost btn-sm"
                >
                  홈으로
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
